import { useState, useEffect, useCallback } from 'react';

interface UseVoiceInputProps {
  onTranscript: (transcript: string) => void;
  disabled?: boolean;
}

interface UseVoiceInputReturn {
  isSupported: boolean;
  isRecording: boolean;
  startRecording: (e: React.MouseEvent) => void;
}

interface SpeechRecognitionResult {
  transcript: string;
}

interface SpeechRecognitionResultList {
  0: SpeechRecognitionResult[];
}

interface SpeechRecognitionEventLocal {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance {
  onresult: ((event: SpeechRecognitionEventLocal) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionWindow extends Window {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

export const useVoiceInput = ({
  onTranscript,
  disabled = false
}: UseVoiceInputProps): UseVoiceInputReturn => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);

  useEffect(() => {
    const recognitionAvailable =
      'SpeechRecognition' in window ||
      ('webkitSpeechRecognition' in window &&
        (window as SpeechRecognitionWindow).webkitSpeechRecognition);

    if (recognitionAvailable) {
      setIsSupported(true);
    } else {
      setIsSupported(false);
      // Silently ignore unsupported browser features
    }
  }, []);

  const startRecording = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (disabled || !isSupported) return;

      const SpeechRecognitionConstructor =
        (window as SpeechRecognitionWindow).SpeechRecognition ||
        (window as SpeechRecognitionWindow).webkitSpeechRecognition;
      const recognition = new SpeechRecognitionConstructor!();

      recognition.onresult = (event: SpeechRecognitionEventLocal) => {
        const transcript = event.results[0][0].transcript;
        onTranscript(transcript);
      };

      recognition.onend = () => {
        recognition.stop();
        setIsRecording(false);
      };

      try {
        recognition.start();
        setIsRecording(true);
      } catch (error) {
        // Silently ignore speech recognition errors
        setIsRecording(false);
      }
    },
    [disabled, isSupported, onTranscript]
  );

  return {
    isSupported,
    isRecording,
    startRecording
  };
};
